import React, { useState, useRef } from 'react';
import { Info } from 'lucide-react';
import { usePopoverPosition, PortaledPopover } from '../ColorPicker';

const TOOLTIP_WIDTH = 220;

/**
 * Small (i) icon that reveals explanatory copy on hover/focus instead of the
 * text sitting inline as a permanent `<p>`/`<div>` block — those hint lines
 * (e.g. "Changing this color updates every object using it") were eating
 * vertical space in already-tight side panels for text most users read once.
 * Uses the same portaled/viewport-clamped popover positioning as the color
 * pickers elsewhere in this app (usePopoverPosition/PortaledPopover from
 * ColorPicker.tsx) so it never gets clipped near a panel edge.
 */
export function InfoTooltip({ text, size = 13 }: { text: string; size?: number }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Height is a rough estimate sized for typical hint copy — the popover
  // measures/clamps against the viewport regardless, so an imperfect guess
  // here only affects which side it opens on, never causes real clipping.
  const { triggerRef, pos } = usePopoverPosition(open, TOOLTIP_WIDTH, 90);

  const show = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  };
  // Small delay so moving the pointer from the icon into the popover itself
  // (to select text, follow a link, etc.) doesn't immediately dismiss it.
  const scheduleHide = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <span
      style={{ display: 'inline-flex', position: 'relative', verticalAlign: 'middle' }}
      onMouseEnter={show}
      onMouseLeave={scheduleHide}
    >
      <button
        ref={triggerRef as React.RefObject<HTMLButtonElement>}
        type="button"
        onFocus={show}
        onBlur={scheduleHide}
        onClick={() => setOpen(o => !o)}
        aria-label="More info"
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: size + 4, height: size + 4, padding: 0, border: 'none', borderRadius: '50%',
          background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer',
        }}
      >
        <Info size={size} strokeWidth={1.75} />
      </button>
      {open && pos && (
        <PortaledPopover top={pos.top} left={pos.left} width={TOOLTIP_WIDTH} onClose={() => setOpen(false)}>
          <div
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
            style={{ fontSize: 12, lineHeight: 1.4, color: 'var(--text)' }}
          >
            {text}
          </div>
        </PortaledPopover>
      )}
    </span>
  );
}
